import React from 'react';
import { LogOut, Mail, Shield, Users as UsersIcon, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { InitialsAvatar } from '../components/ui/InitialsAvatar';

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export const Profile: React.FC = () => {
  const { user, logout } = useAuth();

  const displayName = user?.firstName || user?.name || 'User';
  const rawRole = Array.isArray(user?.roles) ? (user?.roles as any)[0] : user?.roles;
  const role = String(rawRole || 'admin').toLowerCase();
  const memberSince = formatDate(user?.createdAt);

  const infoRows: { icon: React.ElementType; label: string; value: string }[] = [
    { icon: Mail, label: 'Email', value: user?.email || '—' },
    { icon: Shield, label: 'Role', value: role.charAt(0).toUpperCase() + role.slice(1) },
    ...(user?.clientName || user?.tenant?.name
      ? [{ icon: UsersIcon, label: 'Organization', value: user?.clientName || user?.tenant?.name }]
      : []),
    ...(user?.department ? [{ icon: UsersIcon, label: 'Department', value: user.department }] : []),
    ...(memberSince ? [{ icon: Calendar, label: 'Member since', value: memberSince }] : []),
  ];

  return (
    <Page contentClassName="py-6">
      <PageHeader className="py-0" title="Profile" subtitle="Your account details for this workspace." />

      <Section className="py-6">
        <div className="mx-auto max-w-2xl">
          <Card className="space-y-6">
            <div className="flex items-center gap-4">
              <InitialsAvatar name={displayName} className="h-16 w-16 text-xl" />
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold capitalize text-agentic-text-primary">{displayName}</h2>
                <p className="truncate text-sm capitalize text-agentic-text-secondary">{role}</p>
              </div>
            </div>

            <div className="divide-y divide-agentic-gray-200 border-t border-agentic-gray-200">
              {infoRows.map((row) => {
                const Icon = row.icon;
                return (
                  <div key={row.label} className="flex items-center justify-between gap-4 py-3">
                    <span className="flex items-center gap-2 text-sm text-agentic-text-secondary">
                      <Icon className="h-4 w-4" />
                      {row.label}
                    </span>
                    <span className="truncate text-sm font-medium text-agentic-text-primary">{row.value}</span>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end border-t border-agentic-gray-200 pt-5">
              <Button variant="danger" onClick={logout}>
                <span className="flex items-center gap-2">
                  <LogOut className="h-4 w-4" />
                  Sign out
                </span>
              </Button>
            </div>
          </Card>
        </div>
      </Section>
    </Page>
  );
};

export default Profile;
